import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type TopIndividualVoicesView = {
  /** `Largest voice`, with its stance as the badge. */
  kpis: KPIMetric[];
  /** One row per voice, most reach first, one key per sentiment. */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any voice row is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
};

export type TopIndividualVoicesCardProps = WidgetBlockProps & {
  view?: TopIndividualVoicesView;
  /** Opens a voice's posts (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:19575` — named accounts ranked by the reach they carried.
 *
 * Reach rather than mentions, ranked by it, split by the tone of what each
 * account actually posted. A voice matters for how far it carries, not for how
 * often it posts.
 *
 * Stance is **derived from the period**, never stored on the row. An account is
 * an advocate because most of what it posted was positive, which is a reading of
 * what the filters selected — deselect the positive sentiments and an advocate
 * stops being one, which is the honest behaviour.
 */
export function TopIndividualVoicesCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: TopIndividualVoicesCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No named voices in this period"
      {...cardState(status, hasData, onRetry)}
      title="Top individual voices"
      helpText="Named accounts ranked by the reach they carried, with the tone of what each one actually posted."
      helpSupportingText="Reach rather than volume: six posts seen by 800,000 people outrank twenty-four seen by four thousand. Whether an account reads as an advocate or a detractor is worked out from this period's posts, so it can change with the filters."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            stacked
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
